import { computed, Injectable, signal } from '@angular/core';
import { IEstabelecimento } from '@nomoh-monorepo/shared-types';

export interface IPlanoQuotas {
  nome?: string;
  maxProfissionais?: number | null;
  allowRecepcao?: boolean;
  allowAdminExtra?: boolean;
  hasZapIntegration?: boolean;
}

/**
 * Data Service para Estabelecimento - Gestão de estado com Signals
 * Padrão: private _state + public $state (readonly) + computed signals
 */
@Injectable({
  providedIn: 'root',
})
export class EstabelecimentoDataService {
  // State privado
  private _estabelecimentos = signal<IEstabelecimento[]>([]);
  private _estabelecimentoAtual = signal<IEstabelecimento | null>(null);
  private _planoQuotas = signal<IPlanoQuotas | null>(null);
  private _loading = signal(false);
  private _erro = signal<string | null>(null);

  // Signals públicos (readonly)
  $estabelecimentos = this._estabelecimentos.asReadonly();
  $estabelecimentoAtual = this._estabelecimentoAtual.asReadonly();
  $planoQuotas = this._planoQuotas.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();

  $planoSolo = computed(() => this._planoQuotas()?.maxProfissionais === 1);
  $temZapIntegration = computed(
    () => !!this._planoQuotas()?.hasZapIntegration
  );

  // Computed signals
  $estabelecimentosAtivos = computed(() =>
    this._estabelecimentos().filter((e) => e.status === true)
  );

  $totalEstabelecimentos = computed(() => this._estabelecimentos().length);

  // Setters
  setEstabelecimentos(estabelecimentos: IEstabelecimento[]) {
    this._estabelecimentos.set(estabelecimentos);
  }

  setEstabelecimentoAtual(estabelecimento: IEstabelecimento | null) {
    this._estabelecimentoAtual.set(estabelecimento);
  }

  setPlanoQuotas(quotas: IPlanoQuotas | null) {
    this._planoQuotas.set(quotas);
  }

  addEstabelecimento(estabelecimento: IEstabelecimento) {
    this._estabelecimentos.update((current) => [...current, estabelecimento]);
  }

  updateEstabelecimento(id: string, data: Partial<IEstabelecimento>) {
    this._estabelecimentos.update((current) =>
      current.map((e) => (e.id === id ? { ...e, ...data } : e))
    );

    // Atualizar estabelecimentoAtual se for o mesmo
    if (this._estabelecimentoAtual()?.id === id) {
      this._estabelecimentoAtual.update((current) =>
        current ? { ...current, ...data } : null
      );
    }
  }

  removeEstabelecimento(id: string) {
    this._estabelecimentos.update((current) =>
      current.filter((e) => e.id !== id)
    );

    // Limpar estabelecimentoAtual se for o mesmo
    if (this._estabelecimentoAtual()?.id === id) {
      this._estabelecimentoAtual.set(null);
    }
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  limparErro() {
    this._erro.set(null);
  }

  limpar() {
    this._estabelecimentos.set([]);
    this._estabelecimentoAtual.set(null);
    this._planoQuotas.set(null);
    this._loading.set(false);
    this._erro.set(null);
  }
}
